import { motion } from "framer-motion";
import { CalendarClock, Clock, User, Radio, MessageCircle } from "lucide-react";
import radioConfig from "../config/radioConfig";
import { useCurrentShow } from "../hooks/useCurrentShow";

export const Schedule = () => {
  const shows = radioConfig.schedule || [];
  const hasShows = shows.length > 0;
  const currentShow = useCurrentShow(shows);
  const anyLive = Boolean(currentShow);

  return (
    <section id="programacion" className="relative py-20 sm:py-28 bg-[#070A0F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-mono text-cyan-400 mb-3">
            Grilla
          </p>
          <h2 className="font-heading text-3xl sm:text-5xl font-bold text-white">
            Programación
          </h2>
        </motion.div>

        {!anyLive && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10 rounded-2xl border border-emerald-500 bg-[#131C2E] p-6 sm:p-8 shadow-[0_0_40px_rgba(16,185,129,0.2)] flex flex-col md:flex-row items-center gap-6"
          >
            <div className="relative h-32 w-full md:w-48 rounded-xl overflow-hidden bg-slate-900 shrink-0 flex items-center justify-center border border-slate-800">
              <img
                src={radioConfig.assets.logo}
                alt="FM REAL 96.3"
                className="w-full h-full object-contain p-4"
              />
              <div className="absolute top-2 left-2 bg-emerald-500 text-slate-950 font-mono text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1 shadow animate-pulse">
                <Radio size={10} /> Al Aire Ahora
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs uppercase tracking-[0.2em] font-mono text-emerald-400 mb-1">
                Transmisión en vivo 24/7
              </p>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
                La mejor música sonando ahora
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed font-mono">
                Disfrutá de nuestra programación musical continua y la mejor compañía en los 96.3 MHz desde Las Breñas, Chaco.
              </p>
            </div>
          </motion.div>
        )}

        {hasShows ? (
          <div
            data-testid="schedule-section"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {shows.map((show, i) => {
              const liveNow = currentShow === show;
              return (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className={`relative rounded-2xl border bg-[#131C2E] overflow-hidden transition-all duration-300 ${
                    liveNow
                      ? "border-emerald-500 shadow-[0_0_30px_rgba(16,185,129,0.25)] ring-2 ring-emerald-500/50"
                      : "border-slate-800 hover:border-cyan-500/40"
                  }`}
                  data-testid={`schedule-item-${i}`}
                >
                  {liveNow && (
                    <div className="absolute top-3 right-3 z-10 bg-emerald-500 text-slate-950 font-mono text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider flex items-center gap-1.5 shadow-lg animate-pulse">
                      <Radio size={12} /> Al Aire Ahora
                    </div>
                  )}

                  {show.image && (
                    <div className="relative h-48 overflow-hidden bg-gradient-to-br from-slate-900 to-slate-950 flex items-center justify-center">
                      <img
                        src={show.image}
                        alt={show.name}
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/assets/studio.png";
                        }}
                        className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-bold text-white mb-3">
                      {show.name}
                    </h3>
                    <div className="space-y-1.5 text-sm text-slate-400 font-mono">
                      {show.schedule && (
                        <p className="flex items-center gap-2">
                          <Clock size={14} className={liveNow ? "text-emerald-400" : "text-cyan-400"} /> {show.schedule}
                        </p>
                      )}
                      {show.days && (
                        <p className="flex items-center gap-2">
                          <CalendarClock size={14} className={liveNow ? "text-emerald-400" : "text-cyan-400"} /> {show.days}
                        </p>
                      )}
                      {show.hosts && (
                        <p className="flex items-center gap-2">
                          <User size={14} className={liveNow ? "text-emerald-400" : "text-cyan-400"} /> {show.hosts}
                        </p>
                      )}
                    </div>
                    {show.seoDescription && (
                      <p className="text-slate-300 text-xs sm:text-sm leading-relaxed border-t border-slate-800/80 pt-4 mt-4">
                        {show.seoDescription}
                      </p>
                    )}
                    {show.whatsapp && (
                      <div className="mt-4 pt-3 border-t border-slate-800/80">
                        <a
                          href={`https://wa.me/${show.whatsapp}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-xs font-mono font-bold bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-3.5 py-2.5 rounded-xl transition-colors w-full justify-center"
                        >
                          <MessageCircle size={14} /> WhatsApp 3731-438356
                        </a>
                      </div>
                    )}
                  </div>
                </motion.article>
              );
            })}
          </div>
        ) : (
          <motion.div
            data-testid="schedule-section"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl border border-dashed border-slate-700 bg-[#0D131E]/60 backdrop-blur-md p-10 sm:p-16 text-center"
          >
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-6">
              <CalendarClock size={26} className="text-cyan-400" />
            </div>
            <span
              data-testid="schedule-pending-badge"
              className="inline-block px-3 py-1 rounded-full bg-slate-800 text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-4"
            >
              Contenido pendiente de cargar
            </span>
            <p className="max-w-md mx-auto text-slate-400 leading-relaxed">
              Próximamente publicaremos la grilla oficial de programas y horarios de
              FM REAL 96.3.
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Schedule;
